import { computed, effect, inject, untracked } from '@angular/core';
import type { FactoryImage } from '@indutrust/factory-model';
import { withCrudEntitiesEvents, withFileUpload } from '@indutrust/shared-util';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { injectDispatch } from '@ngrx/signals/events';
import { FactoryImageApiClient } from '../factory-image-api-client.service';
import { factoryImageCrudEvents } from './factory-images.events';

type ImageUploadContext = {
  uploadFactoryId: number | null;
  pendingOrderIds: number[] | null;
};

type ContextStore = {
  uploadFactoryId: () => number | null;
};

/**
 * Component-scoped store for the factory-scoped `/images` sub-resource.
 *
 * Provided by {@link provideFactoryEditStores} so each factory editor
 * instance gets a fresh entity collection.
 *
 * Callers MUST pass `urlSegments: { factoryId }` to every CRUD method —
 * the wrapped {@link FactoryImageApiClient} substitutes the segment into
 * the URL template before issuing the request.
 *
 * Binary uploads use {@link withFileUpload}: call `prepareUpload()` with
 * the factoryId, then `stageFiles()` + `commit()`. Files upload sequentially
 * with per-file status; the list reloads automatically once all uploads
 * settle without errors.
 *
 * Drag-to-reorder is **local-only** and deferred until the full factory save.
 * Call `reorderLocal(orderedIds)` to record the desired order. Read
 * `orderedImages()` for the sorted list and `galleryOrderDirty()` to check
 * whether there is an unsaved reorder. Call `resetOrder()` to discard the
 * pending reorder (e.g. on form Undo). `commitOrder()` is called automatically
 * after a successful factory PATCH to lock in the order and clear dirty state.
 *
 * Both `orderedImages` and `galleryOrderDirty` are plain functions that read
 * signals internally — they are reactive when called inside `computed()` or
 * Angular template expressions.
 *
 * @example
 * const imagesStore = inject(FactoryImagesStore);
 * imagesStore.prepareUpload(42);
 * imagesStore.stageFiles([file]);
 * imagesStore.commit();
 */
export const FactoryImagesStore = signalStore(
  withState<ImageUploadContext>({
    uploadFactoryId: null,
    pendingOrderIds: null,
  }),
  withComputed(({ uploadFactoryId }) => ({
    canUploadImages: computed(
      () =>
        typeof uploadFactoryId() === 'number' && (uploadFactoryId() ?? 0) > 0
    ),
  })),
  withMethods((store) => ({
    /**
     * Sets the factoryId context used by the next `commit()`.
     * Call this before `stageFiles()`.
     */
    prepareUpload(factoryId: number): void {
      patchState(store, { uploadFactoryId: factoryId });
    },
  })),
  withFileUpload<FactoryImage>({
    upload: (store) => {
      const client = inject(FactoryImageApiClient);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const ctx = store as ContextStore;
      return (file: File) => {
        const factoryId = ctx.uploadFactoryId();
        return client.upload(factoryId ?? 0, file);
      };
    },
  }),
  withCrudEntitiesEvents<FactoryImage>(factoryImageCrudEvents, () =>
    inject(FactoryImageApiClient)
  ),
  // Ordering helpers occupy the same withMethods slot as the old `reorder` method.
  // orderedImages and galleryOrderDirty are plain functions (not Signals) so that
  // withMethods type inference stays simple. They still read signals internally and
  // are therefore reactive when called inside computed() or template expressions.
  withMethods((store) => {
    const dispatchCrud = injectDispatch(factoryImageCrudEvents);

    function orderedImages(): FactoryImage[] {
      const entities = store.entities();
      const pendingIds = store.pendingOrderIds();
      if (pendingIds === null) {
        // Return a copy, never the store's internal entities reference:
        // PrimeNG's p-orderlist mutates the bound array in place on drag, which
        // would otherwise corrupt entities() and defeat the dirty comparison.
        return [...entities];
      }
      const byId = new Map(entities.map((img) => [img.id, img]));
      const result: FactoryImage[] = [];
      for (const id of pendingIds) {
        const img = byId.get(id);
        if (img) {
          result.push(img);
          byId.delete(id);
        }
      }
      for (const img of byId.values()) {
        result.push(img);
      }
      return result;
    }

    function galleryOrderDirty(): boolean {
      const pendingIds = store.pendingOrderIds();
      if (pendingIds === null) {
        return false;
      }
      const entityIds = store.entities().map((img) => img.id);
      if (pendingIds.length !== entityIds.length) {
        return true;
      }
      return pendingIds.some((id, idx) => id !== entityIds[idx]);
    }

    return {
      /**
       * Returns the image list sorted by the pending user-defined order, falling
       * back to the server order when no pending reorder exists. Any image id no
       * longer present in `entities()` is dropped; images added after the last
       * reorder are appended at the end.
       *
       * Reactive: reads `entities()` and `pendingOrderIds()` signals, so callers
       * inside `computed()` or template expressions will update automatically.
       */
      orderedImages,

      /**
       * Returns true when the user has dragged images into a new order that has
       * not yet been persisted via the factory save.
       *
       * Reactive: reads `pendingOrderIds()` and `entities()` signals.
       */
      galleryOrderDirty,

      /**
       * Records a new desired display order locally without making an API call.
       * The order is persisted as part of the next factory save.
       */
      reorderLocal(orderedIds: number[]): void {
        patchState(store, { pendingOrderIds: orderedIds });
      },

      /**
       * Discards the pending local reorder, reverting to the last saved order.
       */
      resetOrder(): void {
        patchState(store, { pendingOrderIds: null });
      },

      /**
       * Locks the pending local order into the entity collection and clears
       * the dirty flag. Called automatically after a successful factory PATCH.
       */
      commitOrder(): void {
        if (store.pendingOrderIds() === null) {
          return;
        }
        dispatchCrud.loadAllSucceeded(orderedImages());
        patchState(store, { pendingOrderIds: null });
      },
    };
  }),
  withHooks((store) => {
    const dispatchCrud = injectDispatch(factoryImageCrudEvents);
    return {
      onInit() {
        /**
         * Re-fetches the image list after all uploads have settled
         * successfully. Suppressed on initial state (no staged items).
         */
        effect(() => {
          const settled = store.uploadSettled();
          const factoryId = store.uploadFactoryId();
          const hasSuccesses = store
            .uploadStagedItems()
            .some((i) => i.status === 'success');
          if (settled && factoryId !== null && hasSuccesses) {
            untracked(() => {
              dispatchCrud.loadAllRequested({
                urlSegments: { factoryId },
              });
            });
          }
        });
      },
    };
  })
);
